import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';

interface SetupMenuFloatingParams {
  popperElement: HTMLElement | null;
  referenceElement: HTMLElement | null;
  options?: SetupMenuFloatingOptions;
}

interface SetupMenuFloatingOptions {
  offset: 'infinity' | 'overlap';
}

/**
 * Hook that sets up floating menu positioning using Floating UI
 * @param params Object containing popper element, reference element, and infinity mode flag
 * @returns Floating UI configuration object
 */
export const useSetupMenuFloating = ({
  popperElement,
  referenceElement,
  options,
}: SetupMenuFloatingParams) => {
  return useFloating({
    elements: {
      floating: popperElement,
      reference: referenceElement,
    },
    placement: 'right',
    strategy: 'fixed',
    middleware: [
      offset(({ rects }) => {
        // When in "infinity" mode, the menu should be centered above the "reference" element
        if (options?.offset === 'infinity') {
          return {
            mainAxis: -0.5 * (rects.floating.width + rects.reference.width),
          };
        }

        // menu can be placed to the right of the "reference" element, slightly overlapping it
        if (options?.offset === 'overlap') {
          return {
            mainAxis: -8,
          };
        }

        // By default, the menu should be placed to the right of the "reference" element
        return {
          mainAxis: 8,
        };
      }),
      shift({ padding: 8 }),
      flip(),
    ],
    whileElementsMounted: autoUpdate,
  });
};
